import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { hideLoading, showLoading } from '../../actions/api';
import { showNotification } from '../../actions/notifications';
import { StorageItem, StorageKind, StorageItemFull } from '../../constants/document';
import NotificationsTypes from '../../constants/notification';
import documentsService from '../../services/documentsService';
import { forceDownload } from '../../utils';
import DocumentsViewer from '../documents-viewer/DocumentsViewer';
import TreeViewDocuments from '../tree-view/TreeViewDocuments';

const SharedLinkFolderViewer = () => {
  const dispatch = useDispatch();
  const [blobs, setBlobs] = useState<StorageItemFull[]>([]);
  const [fileSelected, setFileSelected] = useState<any>({});

  // Splitter pane state. Unlike DocumentsManager, we do NOT persist the size
  // to localStorage — shared-link visits are typically one-shot, so there's
  // no return-visit benefit to remembering the layout.
  const [panes, setPanes] = useState<SplitterPaneProps[]>(() => [
    { size: '35%', min: '220px' },
    {},
  ]);

  const splitterContainerRef = useRef<HTMLDivElement>(null);
  const treePaneRef = useRef<HTMLDivElement>(null);

  const onSplitterChange = useCallback((e: SplitterOnChangeEvent) => {
    setPanes(e.newState);
  }, []);

  // Same fit-to-content logic as DocumentsManager: measure the natural width
  // of the longest visible row via the rendered `.tree-item-label` spans
  // (which are inline and therefore keep their content-width regardless of
  // the current pane size), then clamp to [220px, 70% of container].
  const fitTreePaneToContent = useCallback(() => {
    const wrapper = treePaneRef.current;
    if (!wrapper) return;
    const treeView = wrapper.querySelector<HTMLElement>('.k-treeview');
    if (!treeView) return;
    const labels = treeView.querySelectorAll<HTMLElement>('.tree-item-label');
    if (labels.length === 0) return;
    const treeLeft = treeView.getBoundingClientRect().left;
    let maxRight = 0;
    labels.forEach((el) => {
      const right = el.getBoundingClientRect().right;
      if (right > maxRight) maxRight = right;
    });
    const naturalWidth = maxRight - treeLeft;
    if (naturalWidth <= 0) return;
    const containerWidth = splitterContainerRef.current?.offsetWidth ?? window.innerWidth;
    const maxWidth = Math.floor(containerWidth * 0.7);
    const padding = 32;
    const finalWidth = Math.min(Math.max(Math.ceil(naturalWidth) + padding, 220), maxWidth);
    setPanes((prev) => {
      const next = [...prev];
      next[0] = { ...next[0], size: `${finalWidth}px` };
      return next;
    });
  }, []);

  useEffect(() => {
    const root = splitterContainerRef.current;
    if (!root) return;
    const splitbar = root.querySelector('.k-splitbar');
    if (!splitbar) return;
    const handler = (e: Event) => {
      e.stopImmediatePropagation();
      e.preventDefault();
      fitTreePaneToContent();
    };
    splitbar.addEventListener('dblclick', handler, true);
    return () => splitbar.removeEventListener('dblclick', handler, true);
  }, [fitTreePaneToContent]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    try {
      dispatch(showLoading());
      var linkCode = window.location.pathname.split('/').pop() ?? '';
      const response = await documentsService.openSharedFolderLink(linkCode);
      if (response) {
        setBlobs(response);
      }
    } catch (error: any) {
      setBlobs([]);
      dispatch(
        showNotification({
          message: 'Requested document is not available, contact with the department which gave the link.',
          type: NotificationsTypes.Error,
        }),
      );
      setTimeout(() => {
        window.location.href = `/?${new Date().getTime()}`;
      }, 2000);
    } finally {
      dispatch(hideLoading());
    }
  };

  // Items returned by openSharedFolderLink are StorageItemFull and already
  // carry a `url`, so we can skip the authenticated `getDocumentInfo` call
  // (which the visitor wouldn't be authorized for anyway). On documents
  // without a url we bail out gracefully — the visitor will see a generic
  // failure notification rather than an opaque silent crash.
  const downloadDocument = useCallback(
    async (item: StorageItem) => {
      try {
        dispatch(showLoading());
        const url = (item as StorageItemFull).url;
        if (!url) throw new Error('No URL on the shared item');
        await forceDownload(url, item.text);
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('[SharedLinkFolderViewer.downloadDocument] failed:', error);
        dispatch(
          showNotification({
            message: `Download failed: ${error?.message ?? 'unknown error'}`,
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
    },
    [dispatch],
  );

  const getDocumentsViewer = () => {
    if (fileSelected != null && fileSelected.kind === StorageKind.document) {
      return <DocumentsViewer {...fileSelected} />;
    } else {
      return (
        <p style={{ fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
          Navigate through the tree and click to preview the file.
        </p>
      );
    }
  };

  return (
    <div ref={splitterContainerRef} style={{ height: '100%' }}>
      <Splitter style={{ height: '100%' }} panes={panes} onChange={onSplitterChange}>
        <div ref={treePaneRef} style={{ height: '100%' }}>
          <TreeViewDocuments
            items={blobs}
            selectFile={setFileSelected}
            addNewFolder={null}
            addNewDocument={null}
            renameDocument={null}
            renameFolder={null}
            deleteDocument={null}
            deleteFolder={null}
            downloadDocument={downloadDocument}
          />
        </div>
        <div style={{ height: '100%' }}>{getDocumentsViewer()}</div>
      </Splitter>
    </div>
  );
};

export default SharedLinkFolderViewer;
